import { compareAsc } from 'date-fns';
import { useMemo } from 'react';

import { TaskView } from '@ndvr/customer-api';

import { useHouseholdMap } from 'views/household/household-queries';

import { TaskSortOption } from '../task-state';

interface UseSortedTaskViewsProps {
    tasks: TaskView[];
    taskSortOption: TaskSortOption;
    sortDirection: 'asc' | 'desc';
}

export const useSortedTaskViews = ({ tasks, taskSortOption, sortDirection }: UseSortedTaskViewsProps) => {
    const householdMap = useHouseholdMap();

    return useMemo(() => {
        if (!tasks.length) return [];

        const tasksCopy = [...tasks];
        const sortMultiplier = sortDirection === 'desc' ? -1 : 1;

        switch (taskSortOption) {
            case TaskSortOption.DUE_DATE:
                return tasksCopy.sort((a, b) => {
                    if (!a.dueDate && !b.dueDate) return 0;
                    if (!a.dueDate) return sortMultiplier;
                    if (!b.dueDate) return -sortMultiplier;
                    return compareAsc(new Date(a.dueDate), new Date(b.dueDate)) * sortMultiplier;
                });

            case TaskSortOption.HOUSEHOLD:
                return tasksCopy.sort((a, b) => {
                    const householdA =
                        (a.householdGUID ? householdMap.get(a.householdGUID)?.globalAccountNamePlain : undefined) || '';
                    const householdB =
                        (b.householdGUID ? householdMap.get(b.householdGUID)?.globalAccountNamePlain : undefined) || '';
                    return householdA.localeCompare(householdB) * sortMultiplier;
                });

            case TaskSortOption.ASSIGNEE:
                return tasksCopy.sort((a, b) => {
                    const nameA = a.assigneeName ?? '';
                    const nameB = b.assigneeName ?? '';

                    return nameA.localeCompare(nameB) * sortMultiplier;
                });

            case TaskSortOption.TASK_TYPE:
                return tasksCopy.sort((a, b) => {
                    const typeA = a.type || '';
                    const typeB = b.type || '';
                    return typeA.localeCompare(typeB) * sortMultiplier;
                });

            default:
                return tasksCopy;
        }
    }, [tasks, taskSortOption, sortDirection, householdMap]);
};
